import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const NavItem = ({ to, icon, label, end = false, onClick }) => (
  <NavLink to={to} end={end} onClick={onClick}
    style={({ isActive }) => ({
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 14px', borderRadius: 8, textDecoration: 'none',
      fontSize: 14, fontWeight: 500,
      color: isActive ? 'var(--accent)' : 'var(--text-secondary)',
      background: isActive ? 'rgba(201, 169, 110, 0.1)' : 'transparent',
      transition: 'all 0.15s', marginBottom: 2
    })}>
    <span style={{ fontSize: 16 }}>{icon}</span>
    {label}
  </NavLink>
);

const SectionLabel = ({ label }) => (
  <div style={{
    fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase',
    letterSpacing: '0.08em', padding: '0 6px', margin: '20px 0 8px'
  }}>{label}</div>
);

const BottomNavItem = ({ to, icon, label, end = false }) => (
  <NavLink to={to} end={end}
    style={({ isActive }) => ({
      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
      padding: '8px 12px', textDecoration: 'none', flex: 1,
      color: isActive ? 'var(--accent)' : 'var(--text-muted)',
      fontSize: 10, fontWeight: 500
    })}>
    <span style={{ fontSize: 20 }}>{icon}</span>
    {label}
  </NavLink>
);

export default function Layout() {
  const { profile, isOwner, isPartner, isAdmin, isSeniorCoach, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [location.pathname]);

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    navigate('/login');
  };

  const roleLabel = isOwner ? 'Company' : isPartner ? 'Partner' : isSeniorCoach ? 'Senior Coach' : 'Coach';
  const base = isAdmin ? '/partner' : isSeniorCoach ? '/senior' : '/coach';
  const myBizBase = isAdmin ? '/partner/my-business' : isSeniorCoach ? '/senior/my-business' : '/coach';

  const closeSidebar = () => setSidebarOpen(false);

  const navContent = (
    <>
      {isAdmin && (
        <>
          <SectionLabel label="HMA Admin" />
          <NavItem to="/partner" icon="◈" label="Dashboard" end onClick={closeSidebar} />
          <NavItem to="/partner/coaches" icon="◉" label="Coaches" onClick={closeSidebar} />
          <NavItem to="/partner/all-clients" icon="◎" label="All Clients" onClick={closeSidebar} />
          <NavItem to="/partner/split-tiers" icon="◑" label="Split Tiers" onClick={closeSidebar} />
          {isOwner && (
            <NavItem to="/partner/admins" icon="⬚" label="Manage Accounts" onClick={closeSidebar} />
          )}
        </>
      )}
      {isSeniorCoach && (
        <>
          <SectionLabel label="My Team" />
          <NavItem to="/senior/team" icon="◉" label="My Coaches" onClick={closeSidebar} />
        </>
      )}
      <SectionLabel label="My Business" />
      <NavItem to={myBizBase} icon="⬡" label="Dashboard" end onClick={closeSidebar} />
      <NavItem to={`${myBizBase}/products`} icon="◎" label="Products" onClick={closeSidebar} />
      <NavItem to={`${myBizBase}/subscriptions`} icon="◆" label="Subscriptions" onClick={closeSidebar} />
      <NavItem to={`${myBizBase}/clients`} icon="◉" label="Clients" onClick={closeSidebar} />
      <NavItem to={`${myBizBase}/transactions`} icon="◇" label="Transactions" onClick={closeSidebar} />
      <NavItem to={`${myBizBase}/reporting`} icon="📊" label="Reporting" onClick={closeSidebar} />
      <NavItem to={`${base}/payment-setup`} icon="⚡" label="Payment Setup" onClick={closeSidebar} />
      <NavItem to={`${base}/settings`} icon="⚙" label="Account Settings" onClick={closeSidebar} />
    </>
  );

  const mobileNav = isAdmin ? [
    { to: '/partner', icon: '◈', label: 'Admin', end: true },
    { to: '/partner/coaches', icon: '◉', label: 'Coaches' },
    { to: '/partner/my-business/products', icon: '◎', label: 'Products' },
    { to: '/partner/my-business/transactions', icon: '◇', label: 'Orders' },
    { to: '/partner/settings', icon: '⚙', label: 'Account' }
  ] : isSeniorCoach ? [
    { to: '/senior/team', icon: '◉', label: 'Team' },
    { to: '/senior/my-business', icon: '⬡', label: 'Dashboard', end: true },
    { to: '/senior/my-business/products', icon: '◎', label: 'Products' },
    { to: '/senior/my-business/transactions', icon: '◇', label: 'Orders' },
    { to: '/senior/settings', icon: '⚙', label: 'Account' }
  ] : [
    { to: '/coach', icon: '⬡', label: 'Dashboard', end: true },
    { to: '/coach/products', icon: '◎', label: 'Products' },
    { to: '/coach/subscriptions', icon: '◆', label: 'Subs' },
    { to: '/coach/transactions', icon: '◇', label: 'Orders' },
    { to: '/coach/settings', icon: '⚙', label: 'Account' }
  ];

  const userInfo = (
    <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 16 }}>
      <div style={{ padding: '0 6px', marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-primary)' }}>{profile?.full_name || 'User'}</div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{profile?.email}</div>
      </div>
      <button onClick={handleSignOut} disabled={signingOut} style={{
        width: '100%', padding: '8px 14px', background: 'transparent',
        border: '1px solid var(--border)', borderRadius: 8,
        color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer', textAlign: 'left'
      }}>
        {signingOut ? 'Signing out...' : '↩ Sign out'}
      </button>
    </div>
  );

  return (
    <div style={{ display: 'flex', minHeight: '100dvh', background: 'var(--bg-primary)' }}>

      {/* Desktop Sidebar */}
      {!isMobile && (
        <aside style={{
          width: 240, background: 'var(--bg-secondary)',
          borderRight: '1px solid var(--border)', display: 'flex',
          flexDirection: 'column', padding: '24px 16px',
          position: 'fixed', height: '100vh', overflowY: 'auto', zIndex: 100
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 40, padding: '0 6px' }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, color: '#0a0a0a', fontSize: 14, flexShrink: 0
            }}>H</div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', lineHeight: 1 }}>HMA Portal</div>
              <div style={{ fontSize: 11, color: 'var(--accent)', marginTop: 2, fontWeight: 500 }}>{roleLabel}</div>
            </div>
          </div>
          <nav style={{ flex: 1 }}>{navContent}</nav>
          {userInfo}
        </aside>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <header style={{
          position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
          background: 'var(--bg-secondary)', borderBottom: '1px solid var(--border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px', height: 56
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 28, height: 28, borderRadius: 7,
              background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, color: '#0a0a0a', fontSize: 13
            }}>H</div>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>HMA Portal</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 500 }}>{roleLabel}</span>
            <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--text-primary)', fontSize: 22, padding: 4
            }}>
              {sidebarOpen ? '✕' : '☰'}
            </button>
          </div>
        </header>
      )}

      {/* Mobile Drawer */}
      {isMobile && sidebarOpen && (
        <>
          <div onClick={closeSidebar} style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            zIndex: 300, backdropFilter: 'blur(2px)'
          }} />
          <div style={{
            position: 'fixed', top: 0, right: 0, bottom: 0, width: 280,
            background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border)',
            zIndex: 400, padding: '24px 16px', overflowY: 'auto',
            display: 'flex', flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{profile?.full_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{profile?.email}</div>
              </div>
              <button onClick={closeSidebar} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: 20, cursor: 'pointer' }}>✕</button>
            </div>
            <nav style={{ flex: 1 }}>{navContent}</nav>
            <button onClick={handleSignOut} disabled={signingOut} style={{
              width: '100%', padding: '10px 14px', background: 'transparent',
              border: '1px solid var(--border)', borderRadius: 8,
              color: 'var(--text-muted)', fontSize: 14, cursor: 'pointer', textAlign: 'left', marginTop: 16
            }}>
              {signingOut ? 'Signing out...' : '↩ Sign out'}
            </button>
          </div>
        </>
      )}

      {/* Main Content */}
      <main style={{
        flex: 1, marginLeft: isMobile ? 0 : 240,
        marginTop: isMobile ? 56 : 0,
        marginBottom: isMobile ? 64 : 0,
        padding: isMobile ? '20px 16px' : '32px 40px',
        overflowY: 'auto', minHeight: '100dvh'
      }}>
        <Outlet />
      </main>

      {/* Mobile Bottom Nav */}
      {isMobile && (
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200,
          background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)',
          display: 'flex', alignItems: 'center',
          paddingBottom: 'env(safe-area-inset-bottom)'
        }}>
          {mobileNav.map((item, i) => <BottomNavItem key={i} {...item} />)}
        </nav>
      )}
    </div>
  );
}
